import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ArrowUp, ArrowUpRight, Mail, MessageCircle } from "lucide-react";
import { MaskedLine, Reveal } from "./Motion";
import { CONTACT, SERVICES } from "../lib/site";
import { submitEnquiry } from "../lib/api";

const schema = z.object({
    name: z.string().min(2, "Your name, so we know who's calling"),
    email: z.string().email("A valid email, please"),
    service: z.string().min(1, "Pick a discipline"),
    message: z.string().min(10, "Give us a line or two"),
});

const inputCls =
    "w-full border border-line bg-card px-4 py-4 font-sans text-sm text-paper placeholder:text-smoke/50 outline-none transition-colors duration-300 focus:border-vermilion rounded-none";

export default function ContactFooter() {
    const [sending, setSending] = useState(false);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ resolver: zodResolver(schema), defaultValues: { service: SERVICES[0].title } });

    const onSubmit = async (values) => {
        setSending(true);
        try {
            await submitEnquiry(values);
            toast.success("Brief received. We'll reply within 24 hours.", {
                description: "Or skip the queue — WhatsApp us directly.",
            });
            reset();
        } catch {
            toast.error("Something broke on the wire. Email us instead.");
        } finally {
            setSending(false);
        }
    };

    return (
        <footer id="contact" data-testid="contact-footer" className="relative border-t border-line px-5 md:px-10 pt-24 md:pt-32 pb-8">
            <Reveal>
                <p className="eyebrow mb-6 flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-vermilion animate-pulse" /> Contact — replies within 24h
                </p>
            </Reveal>

            <h2 className="display-hero font-display font-light text-paper mb-16 md:mb-24">
                <MaskedLine>LET'S TALK</MaskedLine>
            </h2>

            <div className="grid gap-16 lg:grid-cols-2 lg:gap-24 pb-20">
                <form onSubmit={handleSubmit(onSubmit)} data-testid="enquiry-form" className="flex flex-col gap-5" noValidate>
                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <input
                                {...register("name")}
                                data-testid="enquiry-name"
                                placeholder="Your name *"
                                className={inputCls}
                            />
                            {errors.name && (
                                <p data-testid="enquiry-name-error" className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-vermilion">
                                    {errors.name.message}
                                </p>
                            )}
                        </div>
                        <div>
                            <input
                                {...register("email")}
                                data-testid="enquiry-email"
                                placeholder="Email address *"
                                type="email"
                                className={inputCls}
                            />
                            {errors.email && (
                                <p data-testid="enquiry-email-error" className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-vermilion">
                                    {errors.email.message}
                                </p>
                            )}
                        </div>
                    </div>
                    <select {...register("service")} data-testid="enquiry-service" className={`${inputCls} appearance-none cursor-pointer`}>
                        {SERVICES.map((s) => (
                            <option key={s.num} value={s.title} className="bg-card">
                                {s.title}
                            </option>
                        ))}
                        <option value="Something else" className="bg-card">Something else</option>
                    </select>
                    <textarea
                        {...register("message")}
                        data-testid="enquiry-message"
                        placeholder="What are we making famous? *"
                        rows={5}
                        className={`${inputCls} resize-none`}
                    />
                    {errors.message && (
                        <p data-testid="enquiry-message-error" className="font-mono text-[10px] uppercase tracking-[0.15em] text-vermilion">
                            {errors.message.message}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="enquiry-submit"
                        className="group mt-2 flex items-center justify-between border border-vermilion bg-vermilion px-6 py-5 font-mono text-xs uppercase tracking-[0.25em] text-paper transition-all duration-300 hover:bg-transparent hover:text-vermilion disabled:opacity-50"
                    >
                        {sending ? "Sending…" : "Send the brief"}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                </form>

                <div className="flex flex-col justify-between gap-12">
                    <div className="flex flex-col gap-4">
                        <p className="eyebrow mb-2">Skip the form</p>
                        <a
                            href={CONTACT.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp"
                            className="group flex items-center justify-between border border-line px-6 py-6 transition-colors duration-300 hover:border-vermilion"
                        >
                            <span className="flex items-center gap-4">
                                <MessageCircle className="h-5 w-5 text-vermilion" />
                                <span className="font-display text-2xl md:text-3xl font-light text-paper">
                                    WhatsApp us
                                </span>
                            </span>
                            <ArrowUpRight className="h-5 w-5 text-smoke transition-all duration-300 group-hover:text-vermilion group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href={`mailto:${CONTACT.email}`}
                            data-testid="contact-email"
                            className="group flex items-center justify-between border border-line px-6 py-6 transition-colors duration-300 hover:border-vermilion"
                        >
                            <span className="flex items-center gap-4">
                                <Mail className="h-5 w-5 text-vermilion" />
                                <span className="font-display text-2xl md:text-3xl font-light text-paper">
                                    {CONTACT.email}
                                </span>
                            </span>
                            <ArrowUpRight className="h-5 w-5 text-smoke transition-all duration-300 group-hover:text-vermilion group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>

                    <div className="flex flex-col gap-6">
                        <div className="flex items-center gap-6 border-t border-line pt-6" data-testid="contact-socials">
                            {CONTACT.socials.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`contact-social-${s.label.toLowerCase()}`}
                                    className="link-line font-mono text-xs uppercase tracking-[0.2em] text-smoke hover:text-paper transition-colors duration-300"
                                >
                                    {s.label}
                                </a>
                            ))}
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke/60">
                            Media · Digital · Social · PR · Business · Data — {CONTACT.phone}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-4 border-t border-line pt-6 md:flex-row md:items-center md:justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke" data-testid="footer-copyright">
                    © {new Date().getFullYear()} HVP Consultants
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke/60">
                    We turn noise into fame.
                </p>
                <button
                    data-testid="back-to-top"
                    onClick={() =>
                        window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                    className="group flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke hover:text-vermilion transition-colors duration-300"
                >
                    Back to top
                    <ArrowUp className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-1" />
                </button>
            </div>
        </footer>
    );
}
